import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useNavigate } from "react-router-dom";
import {
    FiSearch,
    FiX,
    FiEye,
    FiArrowRight,
    FiArrowLeft,
    FiBookOpen,
    FiTrendingUp,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import SmartImage from "../components/Client/SmartImage";
import ClientAdSense from "../components/Client/ClientAdSense";
import ClientCalling from "../api/ClientCalling";

const LIMIT = 9;

const formatViews = (views) => {
    if (!views) return "0";
    if (views >= 1_000_000) return (views / 1_000_000).toFixed(1) + "M";
    if (views >= 1_000) return (views / 1_000).toFixed(1) + "K";
    return views.toString();
};

const truncate = (text, max) => {
    if (!text) return "";
    return text.length <= max ? text : text.substring(0, max) + "…";
};

const CardSkeleton = ({ featured }) => (
    <div
        className={`rounded-2xl overflow-hidden ios-glass-dark ${
            featured ? "md:col-span-2" : ""
        }`}
    >
        <div className={`${featured ? "h-64 md:h-80" : "h-48 md:h-52"} ios-shimmer`} />
        <div className='p-5 space-y-3'>
            <div className='h-3 w-20 rounded-full ios-shimmer' />
            <div className='h-5 rounded-md ios-shimmer w-5/6' />
            <div className='h-5 rounded-md ios-shimmer w-3/5' />
            <div className='space-y-1.5 pt-1'>
                <div className='h-3 rounded-md ios-shimmer w-full opacity-70' />
                <div className='h-3 rounded-md ios-shimmer w-4/5 opacity-70' />
            </div>
            <div className='h-4 w-24 rounded-full ios-shimmer mt-2' />
        </div>
    </div>
);

const BlogCard = ({ blog, index, onClick, featured }) => (
    <motion.article
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{
            duration: 0.55,
            delay: Math.min(index * 0.06, 0.3),
            ease: [0.22, 1, 0.36, 1],
        }}
        whileHover={{ y: -6 }}
        onClick={onClick}
        className={`group cursor-pointer ios-glass-dark relative rounded-3xl overflow-hidden ${
            featured ? "md:col-span-2" : ""
        }`}
        style={{
            boxShadow:
                "0 14px 36px -14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
        }}
    >
        {/* Image */}
        <div
            className={`relative overflow-hidden ${
                featured ? "h-64 md:h-80" : "h-48 md:h-52"
            }`}
        >
            <SmartImage
                src={blog.main_img}
                alt={blog.main_title}
                className='absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110'
            />
            <div
                className='absolute inset-x-0 bottom-0 h-2/3 pointer-events-none'
                style={{
                    background:
                        "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)",
                }}
            />

            {/* Views badge */}
            <div className='absolute top-3 right-3 ios-glass-dark px-2.5 py-1 rounded-full flex items-center gap-1 text-white text-[10px] md:text-xs font-medium pointer-events-none'>
                <FiEye className='text-xs' style={{ color: "#FCFA02" }} />
                <span className='tabular-nums'>{formatViews(blog.views)}</span>
            </div>

            {/* Featured chip */}
            {featured && (
                <div
                    className='absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white'
                    style={{
                        background: "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                        boxShadow: "0 6px 16px -4px rgba(127,0,255,0.55)",
                    }}
                >
                    <HiSparkles className='text-[11px]' />
                    Featured
                </div>
            )}
        </div>

        {/* Content */}
        <div className='relative p-5 md:p-6'>
            <div
                aria-hidden
                className='absolute -top-12 -right-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-3xl pointer-events-none'
                style={{
                    background:
                        "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                }}
            />
            <div className='relative'>
                <div className='inline-flex items-center gap-1.5 text-client-title text-[10px] uppercase tracking-[0.2em] font-semibold mb-2'>
                    <FiBookOpen className='text-[11px]' />
                    Article
                </div>
                <h3
                    className={`text-client-font font-bold tracking-tight leading-tight group-hover:text-client-title transition-colors ${
                        featured ? "text-xl md:text-2xl" : "text-base md:text-lg"
                    }`}
                    style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                >
                    {blog.main_title}
                </h3>
                <p
                    className='text-client-font/60 text-sm md:text-[15px] leading-relaxed mt-2'
                    style={{
                        display: "-webkit-box",
                        WebkitLineClamp: featured ? 3 : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {truncate(blog.main_description, featured ? 200 : 110)}
                </p>

                <motion.div
                    className='inline-flex items-center gap-1.5 mt-4 text-client-title font-semibold text-xs md:text-sm'
                    whileHover={{ x: 3 }}
                >
                    Read more
                    <FiArrowRight className='group-hover:translate-x-1 transition-transform' />
                </motion.div>
            </div>
        </div>

        {/* Hover ring */}
        <div
            aria-hidden
            className='absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300'
            style={{
                boxShadow:
                    "inset 0 0 0 1.5px rgba(127,0,255,0.45), 0 0 28px rgba(127,0,255,0.3)",
            }}
        />
    </motion.article>
);

const ClientBlog = () => {
    const navigate = useNavigate();

    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(searchQuery.trim()), 350);
        return () => clearTimeout(t);
    }, [searchQuery]);

    const fetchBlogs = useCallback(async (pageNum) => {
        setLoading(true);
        try {
            const resp = await ClientCalling.getBlog(pageNum, LIMIT);
            if (resp?.status && resp?.data) {
                setBlogs(resp.data);
                setHasMore(resp.data.length === LIMIT);
            } else {
                setBlogs([]);
                setHasMore(false);
            }
        } catch (err) {
            console.error("Blog fetch failed:", err);
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const searchBlogs = useCallback(async (q) => {
        setLoading(true);
        try {
            const resp = await ClientCalling.getBlogSearch(q);
            if (resp?.status && resp?.data) setBlogs(resp.data);
            else setBlogs([]);
            setHasMore(false);
        } catch (err) {
            console.error("Blog search failed:", err);
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (debounced) searchBlogs(debounced);
        else fetchBlogs(page);
    }, [debounced, page, fetchBlogs, searchBlogs]);

    const handleClick = (title) => {
        const slug = title.replace(/\s+/g, "-").toLowerCase();
        navigate(`/blog/${slug}`);
    };

    const isSearching = !!debounced;
    const featuredBlog = useMemo(
        () => (!isSearching && page === 1 && blogs[0] ? blogs[0] : null),
        [blogs, isSearching, page]
    );
    const restBlogs = featuredBlog ? blogs.slice(1) : blogs;

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />
            <FixedButton />

            {/* HERO */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 50, -30, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -40, 30, 0], y: [0, 30, -40, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-4xl px-4 sm:px-6 md:px-8 pt-8 md:pt-14 pb-6 md:pb-10 text-center'>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-4'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>Blog</span>
                        </div>
                        <h1 className='text-4xl md:text-6xl font-bold text-client-font tracking-tight leading-tight'>
                            Stories,{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                insights & ideas
                            </span>
                        </h1>
                        <p className='text-client-font/60 text-sm md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed'>
                            Latest articles on AI prompts, creative techniques, and the art
                            of digital storytelling.
                        </p>
                    </motion.div>

                    {/* Search */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className='mt-6 md:mt-8 max-w-xl mx-auto'
                    >
                        <div
                            className='flex items-center gap-2 md:gap-3 rounded-2xl px-3 md:px-4 py-2 md:py-2.5'
                            style={{
                                background: "rgba(255,255,255,0.06)",
                                backdropFilter: "blur(22px) saturate(180%)",
                                WebkitBackdropFilter: "blur(22px) saturate(180%)",
                                border: focused
                                    ? "1px solid rgba(127,0,255,0.6)"
                                    : "1px solid rgba(255,255,255,0.14)",
                                boxShadow: focused
                                    ? "0 14px 40px -10px rgba(127,0,255,0.5), inset 0 1px 0 rgba(255,255,255,0.16)"
                                    : "0 10px 28px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)",
                                transition: "border 0.25s, box-shadow 0.25s",
                            }}
                        >
                            <FiSearch
                                className='text-lg md:text-xl flex-shrink-0'
                                style={{
                                    color: focused ? "#FCFA02" : "rgba(255,255,255,0.6)",
                                }}
                            />
                            <input
                                type='text'
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => setFocused(true)}
                                onBlur={() => setFocused(false)}
                                placeholder='Search articles…'
                                className='flex-1 bg-transparent outline-none border-0 text-client-font placeholder:text-client-font/40 text-sm md:text-base py-2'
                            />
                            <AnimatePresence>
                                {searchQuery && (
                                    <motion.button
                                        initial={{ opacity: 0, scale: 0.7 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.7 }}
                                        whileTap={{ scale: 0.9 }}
                                        whileHover={{ rotate: 90 }}
                                        onClick={() => setSearchQuery("")}
                                        aria-label='Clear'
                                        className='w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center'
                                        style={{
                                            background: "rgba(255,255,255,0.08)",
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            color: "rgba(255,255,255,0.75)",
                                        }}
                                    >
                                        <FiX className='text-sm' />
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </div>
                        {isSearching && (
                            <motion.p
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                className='mt-3 text-client-font/55 text-[11px] md:text-xs'
                            >
                                Searching for:{" "}
                                <span className='text-client-title font-semibold'>
                                    "{debounced}"
                                </span>
                            </motion.p>
                        )}
                    </motion.div>
                </div>
            </section>

            {/* BLOG GRID */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-7xl'>
                    {/* DSPY1 — top-of-grid display ad, lazy-loaded */}
                    <ClientAdSense
                        slot='9729451986'
                        lazyLoad
                        containerClassName='w-full mb-6 md:mb-10'
                    />

                    {/* Section header */}
                    {!loading && blogs.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex items-center justify-between mb-5 md:mb-6 mt-6 md:mt-10'
                        >
                            <div className='flex items-center gap-2'>
                                <div
                                    className='w-1 h-6 rounded-full'
                                    style={{
                                        background:
                                            "linear-gradient(180deg, #7F00FF, #FCFA02)",
                                    }}
                                />
                                <h2 className='text-client-font font-semibold text-sm md:text-base tracking-wide uppercase'>
                                    {isSearching ? "Search Results" : "Latest Articles"}
                                </h2>
                            </div>
                            <div className='ios-glass px-3 py-1 rounded-full text-client-font/75 text-[10px] md:text-xs inline-flex items-center gap-1.5'>
                                <FiTrendingUp style={{ color: "#FCFA02" }} />
                                <span className='tabular-nums font-bold text-client-title'>
                                    {blogs.length}
                                </span>
                                <span>
                                    {blogs.length === 1 ? "article" : "articles"}
                                </span>
                            </div>
                        </motion.div>
                    )}

                    {/* Loading skeleton */}
                    {loading && (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6'>
                            {!isSearching && page === 1 && <CardSkeleton featured />}
                            {[...Array(isSearching || page > 1 ? 9 : 7)].map((_, i) => (
                                <CardSkeleton key={i} />
                            ))}
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && blogs.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex flex-col items-center justify-center text-center py-16 md:py-24'
                        >
                            <div
                                className='w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center mb-5'
                                style={{
                                    background:
                                        "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.08))",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    boxShadow:
                                        "0 12px 28px -10px rgba(127,0,255,0.45)",
                                }}
                            >
                                {isSearching ? (
                                    <FiSearch className='text-2xl md:text-3xl text-client-title' />
                                ) : (
                                    <FiBookOpen className='text-2xl md:text-3xl text-client-title' />
                                )}
                            </div>
                            <h3 className='text-client-font font-bold text-lg md:text-2xl tracking-tight'>
                                {isSearching
                                    ? `No results for "${debounced}"`
                                    : "No articles yet"}
                            </h3>
                            <p className='text-client-font/55 text-sm md:text-base mt-2 max-w-md'>
                                {isSearching
                                    ? "Try a different keyword or clear your search."
                                    : "Check back soon for fresh stories and insights."}
                            </p>
                            {isSearching && (
                                <motion.button
                                    whileHover={{ y: -2, scale: 1.04 }}
                                    whileTap={{ scale: 0.96 }}
                                    onClick={() => setSearchQuery("")}
                                    className='mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-semibold text-xs md:text-sm'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        boxShadow:
                                            "0 12px 28px -8px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                    }}
                                >
                                    Clear search
                                </motion.button>
                            )}
                        </motion.div>
                    )}

                    {/* Grid */}
                    {!loading && blogs.length > 0 && (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6'>
                            {featuredBlog && (
                                <BlogCard
                                    key={featuredBlog._id}
                                    blog={featuredBlog}
                                    index={0}
                                    featured
                                    onClick={() => handleClick(featuredBlog.main_title)}
                                />
                            )}
                            {restBlogs.map((b, idx) => (
                                <BlogCard
                                    key={b._id}
                                    blog={b}
                                    index={idx + (featuredBlog ? 1 : 0)}
                                    onClick={() => handleClick(b.main_title)}
                                />
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && !isSearching && blogs.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className='flex justify-center items-center gap-2 md:gap-3 mt-10 md:mt-14'
                        >
                            <motion.button
                                whileHover={page > 1 ? { x: -3, scale: 1.04 } : {}}
                                whileTap={page > 1 ? { scale: 0.96 } : {}}
                                onClick={() => page > 1 && setPage(page - 1)}
                                disabled={page === 1}
                                className='ios-glass-btn inline-flex items-center gap-1.5 px-4 py-2 md:px-5 md:py-2.5 rounded-full text-client-font text-xs md:text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed'
                            >
                                <FiArrowLeft />
                                Prev
                            </motion.button>
                            <div
                                className='inline-flex items-center justify-center min-w-[44px] h-10 md:min-w-[48px] md:h-11 rounded-full text-white text-sm md:text-base font-bold tabular-nums'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                    boxShadow:
                                        "0 10px 24px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
                                }}
                            >
                                {page}
                            </div>
                            <motion.button
                                whileHover={hasMore ? { x: 3, scale: 1.04 } : {}}
                                whileTap={hasMore ? { scale: 0.96 } : {}}
                                onClick={() => hasMore && setPage(page + 1)}
                                disabled={!hasMore}
                                className='ios-glass-btn inline-flex items-center gap-1.5 px-4 py-2 md:px-5 md:py-2.5 rounded-full text-client-font text-xs md:text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed'
                            >
                                Next
                                <FiArrowRight />
                            </motion.button>
                        </motion.div>
                    )}
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default ClientBlog;
